import { useState, useEffect } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import ResourceManager from "@/components/common/ResourceManager";
import { eur, fdate, km } from "@/lib/format";

const ALL = "all";

const vehLabel = (id, lookups) => {
  const v = (lookups.vehicles || []).find((x) => x.id === id);
  return v ? `${v.brand} ${v.model} (${v.registration})` : "—";
};

function FuelAnalytics() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(ALL);
  const [vehicle, setVehicle] = useState(ALL);
  const [ftype, setFtype] = useState(ALL);

  useEffect(() => {
    const p = new URLSearchParams();
    if (month !== ALL) p.set("month", month);
    if (vehicle !== ALL) p.set("vehicle_id", vehicle);
    if (ftype !== ALL) p.set("fuel_type", ftype);
    api.get(`/fuel/analytics?${p.toString()}`).then((r) => setData(r.data)).catch(() => {});
  }, [month, vehicle, ftype]);

  if (!data) return <div className="py-10 text-center text-sm text-[#8695AB]">Chargement…</div>;
  const k = data.kpis;
  const kpis = [
    ["Dépenses du mois", eur(k.month_amount)], ["Cumul année (YTD)", eur(k.ytd_amount)],
    ["Litres consommés", `${k.total_liters} L`], ["Prix moyen", `${k.avg_price} €/L`],
    ["Coût moyen / véhicule", eur(k.avg_per_vehicle)], ["Véhicules", k.vehicles],
  ];

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        <Select value={month} onValueChange={setMonth}><SelectTrigger className="w-40 bg-white" data-testid="fuel-filter-month"><SelectValue placeholder="Mois" /></SelectTrigger><SelectContent><SelectItem value={ALL}>Tous les mois</SelectItem>{data.options.months.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
        <Select value={vehicle} onValueChange={setVehicle}><SelectTrigger className="w-56 bg-white" data-testid="fuel-filter-vehicle"><SelectValue placeholder="Véhicule" /></SelectTrigger><SelectContent><SelectItem value={ALL}>Tous les véhicules</SelectItem>{data.options.vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}</SelectContent></Select>
        <Select value={ftype} onValueChange={setFtype}><SelectTrigger className="w-44 bg-white" data-testid="fuel-filter-type"><SelectValue placeholder="Carburant" /></SelectTrigger><SelectContent><SelectItem value={ALL}>Tous carburants</SelectItem>{data.options.fuel_types.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent></Select>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {kpis.map(([l, v]) => <div key={l} className="rounded-2xl border border-[#E2E8F0] bg-white p-4"><div className="text-[11px] uppercase text-[#8695AB]">{l}</div><div className="mt-1 font-heading text-xl font-bold text-[#18221F]">{v}</div></div>)}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
          <h3 className="font-heading text-base font-semibold text-[#18221F]">Évolution des dépenses & litres</h3>
          <div className="mt-4 h-64"><ResponsiveContainer width="100%" height="100%"><AreaChart data={data.monthly}><defs><linearGradient id="fg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#001A3B" stopOpacity={0.25} /><stop offset="100%" stopColor="#001A3B" stopOpacity={0} /></linearGradient></defs><CartesianGrid strokeDasharray="3 3" stroke="#EDF1F6" vertical={false} /><XAxis dataKey="month" tick={{ fontSize: 11, fill: "#8695AB" }} axisLine={false} tickLine={false} /><YAxis tick={{ fontSize: 11, fill: "#8695AB" }} axisLine={false} tickLine={false} width={50} /><Tooltip formatter={(v, n) => n === "amount" ? eur(v) : `${v} L`} /><Area type="monotone" dataKey="amount" name="Montant" stroke="#001A3B" strokeWidth={2.5} fill="url(#fg)" /></AreaChart></ResponsiveContainer></div>
        </div>
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
          <h3 className="font-heading text-base font-semibold text-[#18221F]">Dépenses carburant les plus élevées</h3>
          <div className="mt-4 h-64"><ResponsiveContainer width="100%" height="100%"><BarChart data={data.top_spenders} margin={{ left: 6 }}><CartesianGrid strokeDasharray="3 3" stroke="#EDF1F6" vertical={false} /><XAxis dataKey="registration" tick={{ fontSize: 10, fill: "#8695AB" }} axisLine={false} tickLine={false} /><YAxis tick={{ fontSize: 11, fill: "#8695AB" }} axisLine={false} tickLine={false} width={50} /><Tooltip formatter={(v) => eur(v)} /><Bar dataKey="amount" fill="#A5894E" radius={[5, 5, 0, 0]} /></BarChart></ResponsiveContainer></div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
          <h3 className="mb-3 font-heading text-base font-semibold text-[#18221F]">Consommation la plus élevée (litres)</h3>
          {data.top_consumers.map((t, i) => (<div key={i} className="flex items-center justify-between border-b border-[#EDF0F5] py-2 text-sm last:border-0"><span className="text-[#18221F]">{t.label} <span className="font-mono text-xs text-[#8695AB]">{t.registration}</span></span><span className="font-semibold">{t.liters} L</span></div>))}
          {data.top_consumers.length === 0 && <p className="py-6 text-center text-sm text-[#8695AB]">Aucune donnée</p>}
        </div>
        <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6">
          <h3 className="mb-3 font-heading text-base font-semibold text-[#18221F]">Répartition par type de carburant</h3>
          {data.by_fuel_type.map((t, i) => (<div key={i} className="flex items-center justify-between border-b border-[#EDF0F5] py-2 text-sm last:border-0"><span className="text-[#18221F]">{t.label}</span><span className="font-semibold">{eur(t.amount)}</span></div>))}
          {data.by_fuel_type.length === 0 && <p className="py-6 text-center text-sm text-[#8695AB]">Aucune donnée</p>}
        </div>
      </div>
    </div>
  );
}

export default function Fuel() {
  const [tab, setTab] = useState("analyse");

  const fuelCols = [
    { key: "date", label: "Date", render: (r) => fdate(r.date) },
    { key: "vehicle_id", label: "Véhicule", render: (r, l) => vehLabel(r.vehicle_id, l) },
    { key: "station", label: "Station" },
    { key: "liters", label: "Litres", render: (r) => `${r.liters} L` },
    { key: "price_per_liter", label: "Prix/L", render: (r) => `${(r.price_per_liter || 0).toFixed(3)} €` },
    { key: "amount", label: "Montant", render: (r) => <span className="font-semibold">{eur(r.amount)}</span> },
    { key: "mileage", label: "Km", render: (r) => km(r.mileage) },
  ];
  const fuelFields = [
    { key: "vehicle_id", label: "Véhicule", type: "vehicle", full: true },
    { key: "date", label: "Date", type: "date" },
    { key: "station", label: "Station" },
    { key: "fuel_type", label: "Type de carburant", type: "select", options: ["Diesel", "Essence", "Hybride"] },
    { key: "liters", label: "Litres", type: "number" },
    { key: "price_per_liter", label: "Prix / litre (€)", type: "number" },
    { key: "amount", label: "Montant (€)", type: "number" },
    { key: "mileage", label: "Kilométrage", type: "number" },
  ];

  const chargeCols = [
    { key: "date", label: "Date", render: (r) => fdate(r.date) },
    { key: "vehicle_id", label: "Véhicule", render: (r, l) => vehLabel(r.vehicle_id, l) },
    { key: "station", label: "Borne" },
    { key: "kwh", label: "kWh", render: (r) => `${r.kwh} kWh` },
    { key: "duration_min", label: "Durée", render: (r) => `${r.duration_min} min` },
    { key: "amount", label: "Coût", render: (r) => <span className="font-semibold">{eur(r.amount)}</span> },
    { key: "mileage", label: "Km", render: (r) => km(r.mileage) },
  ];
  const chargeFields = [
    { key: "vehicle_id", label: "Véhicule", type: "vehicle", full: true },
    { key: "date", label: "Date", type: "date" },
    { key: "station", label: "Borne" },
    { key: "kwh", label: "kWh", type: "number" },
    { key: "duration_min", label: "Durée (min)", type: "number" },
    { key: "amount", label: "Coût (€)", type: "number" },
    { key: "mileage", label: "Kilométrage", type: "number" },
  ];

  return (
    <div>
      <PageHeader title="Carburant & Recharge" subtitle="Analyse des consommations, saisie manuelle et recharge électrique." />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="analyse" data-testid="fuel-tab-analyse">📊 Analyse</TabsTrigger>
          <TabsTrigger value="fuel" data-testid="fuel-tab-carburant">⛽ Carburant</TabsTrigger>
          <TabsTrigger value="charge" data-testid="fuel-tab-recharge">🔌 Recharge électrique</TabsTrigger>
        </TabsList>
        <TabsContent value="analyse">
          <FuelAnalytics />
        </TabsContent>
        <TabsContent value="fuel">
          <ResourceManager title="Carburant" subtitle="Opérations carte carburant." resource="fuel-transactions" exportCollection="fuel_transactions" columns={fuelCols} fields={fuelFields} lookupsNeeded={["vehicles"]} defaultForm={{ fuel_type: "Diesel" }} />
        </TabsContent>
        <TabsContent value="charge">
          <ResourceManager title="Recharge électrique" subtitle="Sessions de recharge des véhicules électriques." resource="charging-transactions" exportCollection="charging_transactions" columns={chargeCols} fields={chargeFields} lookupsNeeded={["vehicles"]} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
